import React, { useState, useRef } from 'react';
import {
  convertImageToVideo,
  downloadVideo,
  validateImageFile,
  MOTION_STYLES,
  DURATION_OPTIONS,
  RESOLUTION_OPTIONS,
} from '../../services/imageToVideo';
import ImageUpscaler from './ImageUpscaler';

const AIVideoCreator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Image to Video 상태
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [motionStyle, setMotionStyle] = useState('zoom_in');
  const [img2videoDuration, setImg2videoDuration] = useState(5);
  const [img2videoResolution, setImg2videoResolution] = useState('1080p');
  const [convertProgress, setConvertProgress] = useState(0);
  const [convertMessage, setConvertMessage] = useState('');
  const [videoResult, setVideoResult] = useState(null);
  const fileInputRef = useRef(null);

  // Image to Video 핸들러
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      return;
    }

    setImageFile(file);
    setError(null);
    setVideoResult(null);

    // 이미지 미리보기 생성
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const fakeEvent = { target: { files: [file] } };
      handleImageUpload(fakeEvent);
    }
  };

  const handleConvertToVideo = async () => {
    if (!imageFile) {
      setError('이미지를 업로드해주세요.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setConvertProgress(0);
    setConvertMessage('');

    try {
      const result = await convertImageToVideo({
        imageFile,
        motionStyle,
        duration: img2videoDuration,
        resolution: img2videoResolution,
        onProgress: (progress, message) => {
          setConvertProgress(progress);
          setConvertMessage(message);
        },
      });

      setVideoResult(result);
    } catch (err) {
      setError(err.message || '영상 변환 중 오류가 발생했습니다.');
    } finally {
      setIsGenerating(false);
      setConvertProgress(0);
      setConvertMessage('');
    }
  };

  const handleDownloadVideo = () => {
    if (videoResult?.videoUrl) {
      downloadVideo(videoResult.videoUrl, `${imageFile?.name.replace(/\.[^/.]+$/, '')}_video.mp4`);
    } else {
      downloadVideo(null);
    }
  };

  const handleResetImg2Video = () => {
    setImageFile(null);
    setImagePreview(null);
    setVideoResult(null);
    setError(null);
    setConvertProgress(0);
    setConvertMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-[900px] mx-auto p-5">
      {/* 이미지 업스케일링 */}
      <div className="mb-6">
        <ImageUpscaler />
      </div>

      {/* 이미지 영상 변환 */}
      <div className="mb-6">
        <header className="mb-4">
          <h2 className="text-lg text-gray-900 mb-1">🎞️ 이미지 영상 변환</h2>
          <p className="text-sm text-gray-500">정적인 이미지를 자연스러운 영상으로 변환합니다.</p>
        </header>
        <div>
          {/* 이미지 업로드 섹션 */}
          {!videoResult && (
            <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
              <div className="mb-4">
                <h2 className="text-lg text-gray-900 mb-1">이미지 업로드</h2>
                <p className="text-sm text-gray-500">정적 이미지를 영상으로 변환합니다. (JPG, PNG, WEBP / 최대 10MB)</p>
              </div>

              <div
                className="min-h-[300px] border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50 cursor-pointer transition-all relative overflow-hidden hover:border-blue-500 hover:bg-gray-100"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div className="relative w-full h-full min-h-[300px] group">
                    <img src={imagePreview} alt="업로드된 이미지" className="w-full h-full object-contain max-h-[500px]" />
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        className="px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold cursor-pointer transition-transform hover:scale-105"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetImg2Video();
                        }}
                      >
                        다른 이미지 선택
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center min-h-[300px] p-10">
                    <span className="text-6xl mb-4 opacity-60">📷</span>
                    <p className="text-lg font-semibold text-gray-900 mb-2">이미지를 드래그하거나 클릭하여 업로드</p>
                    <p className="text-sm text-gray-500">JPG, PNG, WEBP 지원 (최대 10MB)</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/webp"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </section>
          )}

          {/* 설정 섹션 */}
          {imageFile && !videoResult && (
            <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
              {/* 모션 스타일 선택 */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-900 mb-3">모션 스타일</h3>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2.5">
                  {Object.values(MOTION_STYLES).map((style) => (
                    <button
                      key={style.id}
                      className={`flex flex-col items-start p-3.5 rounded-lg cursor-pointer transition-all text-left ${
                        motionStyle === style.id
                          ? 'bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-2 border-blue-500'
                          : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200 hover:border-blue-500'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      onClick={() => setMotionStyle(style.id)}
                      disabled={isGenerating}
                    >
                      <span className="font-semibold text-gray-900 text-sm mb-1">{style.name}</span>
                      <span className="text-xs text-gray-500">{style.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 영상 길이 선택 */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-900 mb-3">영상 길이</h3>
                <div className="flex gap-2.5">
                  {DURATION_OPTIONS.map((duration) => (
                    <button
                      key={duration}
                      className={`flex-1 py-3 rounded-lg cursor-pointer transition-all text-lg font-semibold ${
                        img2videoDuration === duration
                          ? 'bg-blue-500 text-white border-2 border-blue-500'
                          : 'bg-gray-100 text-gray-900 border-2 border-transparent hover:bg-gray-200 hover:border-blue-500'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      onClick={() => setImg2videoDuration(duration)}
                      disabled={isGenerating}
                    >
                      {duration}초
                    </button>
                  ))}
                </div>
              </div>

              {/* 해상도 선택 */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-gray-900 mb-3">출력 해상도</h3>
                <div className="flex gap-2.5">
                  {Object.values(RESOLUTION_OPTIONS).map((res) => (
                    <button
                      key={res.id}
                      className={`flex-1 flex flex-col items-center py-3 rounded-lg cursor-pointer transition-all ${
                        img2videoResolution === res.id
                          ? 'bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-2 border-blue-500'
                          : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200 hover:border-blue-500'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      onClick={() => setImg2videoResolution(res.id)}
                      disabled={isGenerating}
                    >
                      <span className="font-semibold text-gray-900 text-sm mb-1">{res.name}</span>
                      <span className="text-xs text-gray-500">{res.width} x {res.height}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 변환 버튼 */}
              <button
                className="w-full mt-4 py-3 px-6 text-base font-semibold bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none rounded-lg cursor-pointer transition-all hover:enabled:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleConvertToVideo}
                disabled={isGenerating}
              >
                {isGenerating ? '변환 중...' : '영상 변환 시작'}
              </button>
            </section>
          )}

          {/* 변환 진행 상태 */}
          {isGenerating && (
            <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
              <h2 className="text-lg text-gray-900 mb-4">영상 변환 중...</h2>
              <div className="mt-4">
                <div className="w-full h-2 bg-gray-200 rounded overflow-hidden mb-3">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-[width] duration-300"
                    style={{ width: `${convertProgress}%` }}
                  ></div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg text-blue-500">{convertProgress}%</span>
                  <span className="text-sm text-gray-500">{convertMessage}</span>
                </div>
              </div>
            </section>
          )}

          {/* 에러 메시지 */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg mb-6 text-sm">
              <span className="text-lg">⚠️</span>
              {error}
            </div>
          )}

          {/* 결과 섹션 */}
          {videoResult && (
            <section className="bg-white rounded-2xl p-6 shadow-sm mb-6">
              <h2 className="text-xl text-gray-900 mb-4">영상 변환 완료!</h2>
              <div className="grid grid-cols-[1fr_1.2fr] gap-6 md:grid-cols-1">
                <div className="aspect-[9/16] max-h-[350px] bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center">
                  {videoResult.videoUrl ? (
                    <video
                      src={videoResult.videoUrl}
                      controls
                      className="w-full h-full object-contain"
                      poster={videoResult.thumbnailUrl}
                    />
                  ) : (
                    <div className="text-center text-white relative">
                      <img
                        src={videoResult.thumbnailUrl}
                        alt="영상 썸네일"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center text-white text-center p-5">
                        <span className="text-4xl block mb-2">🎬</span>
                        <p>미리보기</p>
                        <p className="text-xs text-gray-500 mt-2">
                          (API 연동 후 실제 영상이 재생됩니다)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-4">
                  <div>
                    <h3 className="text-lg text-gray-900 mb-1.5">변환 완료</h3>
                    <p className="text-sm text-gray-500 mb-3">
                      {videoResult.metadata.originalImage}을(를) 영상으로 변환했습니다.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2.5">
                    <div className="p-2.5 bg-gray-100 rounded-lg text-center">
                      <span className="block text-xs text-gray-500">모션</span>
                      <span className="block text-sm font-semibold text-gray-900 mt-0.5">{videoResult.metadata.motionStyle}</span>
                    </div>
                    <div className="p-2.5 bg-gray-100 rounded-lg text-center">
                      <span className="block text-xs text-gray-500">길이</span>
                      <span className="block text-sm font-semibold text-gray-900 mt-0.5">{videoResult.metadata.duration}</span>
                    </div>
                    <div className="p-2.5 bg-gray-100 rounded-lg text-center">
                      <span className="block text-xs text-gray-500">해상도</span>
                      <span className="block text-sm font-semibold text-gray-900 mt-0.5">{videoResult.metadata.resolution}</span>
                    </div>
                  </div>

                  <div className="flex gap-2.5 mt-auto">
                    <button
                      className="flex-1 py-3 text-sm font-semibold border-none rounded-lg cursor-pointer transition-opacity bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleDownloadVideo}
                    >
                      다운로드
                    </button>
                    <button
                      className="flex-1 py-3 text-sm font-semibold border-2 border-gray-200 rounded-lg cursor-pointer transition-all bg-gray-500 text-white hover:enabled:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleResetImg2Video}
                    >
                      새 영상 만들기
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* 기능 안내 */}
          {!imageFile && !videoResult && (
            <section className="bg-gray-100 rounded-2xl p-6 mt-6">
              <h3 className="text-base text-gray-900 mb-4">이미지 영상 변환 과정</h3>
              <div className="flex gap-4 md:flex-col">
                <div className="flex-1 flex gap-3 items-start">
                  <span className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</span>
                  <div>
                    <h4 className="text-sm text-gray-900 mb-1">이미지 업로드</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">정적인 이미지를 업로드합니다.</p>
                  </div>
                </div>
                <div className="flex-1 flex gap-3 items-start">
                  <span className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</span>
                  <div>
                    <h4 className="text-sm text-gray-900 mb-1">모션 설정</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">원하는 카메라 움직임과 영상 설정을 선택합니다.</p>
                  </div>
                </div>
                <div className="flex-1 flex gap-3 items-start">
                  <span className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</span>
                  <div>
                    <h4 className="text-sm text-gray-900 mb-1">영상 생성</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">AI가 자연스러운 움직임의 영상을 생성합니다.</p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIVideoCreator;
